// src/features/media/components/ClipToolContent.tsx

import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  Stack,
  Group,
  Text,
  TextInput,
  Select,
  Button,
  Alert,
  AspectRatio,
  Divider,
} from '@mantine/core';
import { IconInfoCircle, IconKeyboard } from '@tabler/icons-react';
import { MediaPlayerLinker } from '@lib2/model/player/PlayerLinker';
import { useResellerBrand } from '@/shared/hooks/useResellerBrand';
import { MonetizeUtils } from '@/features/monetize/MonetizeUtils';
import { usePreviewGrant } from '@/features/monetize/usePreviewGrant';
import { PlayerMessageUtil } from '@lib2/model/player/postmessage/PlayerMessageUtil';
import { PlayerMessageType } from '@lib2/model/util/browser/PostMessageUtil';
import { bcLong } from '@lib2/model/util/string/BaseConverter';
import { usePlayers } from '@/features/players/usePlayers';
import { useTranscodeVods } from '../useTranscodeVods';
import { useClipMutation } from '../useClipMutation';
import { usePlayerTime } from '../hooks/usePlayerTime';
import { MediaUtils } from '../MediaUtils';
import { ClipRangeSelector } from './ClipRangeSelector';
import { VIDEO_CONSTRAINTS } from '@/lib/constants/media';
import type { VideoViewData, ClipCreateRequest } from '@/lib/types/Media';
import type IMediaView from '@lib2/model/media/IMediaView';

interface ClipToolContentProps {
  video: VideoViewData;
  onSuccess: (newVideo: VideoViewData) => void;
  onCancel: () => void;
}

export function ClipToolContent({
  video,
  onSuccess,
  onCancel,
}: ClipToolContentProps) {
  const { players, defaultPlayerId } = usePlayers();
  const isReseller = useResellerBrand();
  const { data: transcodeVods, isLoading: vodsLoading } = useTranscodeVods();
  const clipMutation = useClipMutation();

  // Scoped to THIS iframe: the detail page behind the modal mounts its own
  // player for the same media, and both post time_change to this window.
  const previewIframeRef = useRef<HTMLIFrameElement>(null);
  const { currentTime } = usePlayerTime(previewIframeRef);

  const duration = video.media.duration;

  // The clip preview has to be able to jump anywhere in the source. A player
  // with "Allow seek forward" off snaps the playhead back to the
  // furthest-watched second after every seek (VideoJSPlayer seeked handler),
  // which reads as a preview that ignores the clip points. Fall back to any
  // seek-capable player rather than inheriting that restriction here.
  const previewPlayerId = useMemo(() => {
    const preferred = players.find((p) => p.core.playerId === defaultPlayerId);
    if (!preferred || preferred.settings.allowSeekForward) return defaultPlayerId;
    return (
      players.find((p) => p.settings.allowSeekForward)?.core.playerId ??
      defaultPlayerId
    );
  }, [players, defaultPlayerId]);

  // Clip state
  const [startSecond, setStartSecond] = useState(0);
  const [endSecond, setEndSecond] = useState(duration);
  const [title, setTitle] = useState(`Clip of ${video.media.title}`);
  const [selectedVodId, setSelectedVodId] = useState<string | null>(null);

  // The I/O handler reads live values through refs so the window listener binds
  // once, instead of being torn down and re-added on every player time update.
  const stateRef = useRef({ currentTime, startSecond, endSecond, duration });
  stateRef.current = { currentTime, startSecond, endSecond, duration };

  // Keyboard shortcuts for I/O
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore when typing in inputs
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      // No capture before the player has reported a time (currentTime null).
      const s = stateRef.current;
      if (e.key === 'i' || e.key === 'I') {
        e.preventDefault();
        if (s.currentTime === null) return;
        const newStart = Math.floor(s.currentTime);
        if (newStart < s.endSecond) {
          setStartSecond(newStart);
        }
      }
      if (e.key === 'o' || e.key === 'O') {
        e.preventDefault();
        if (s.currentTime === null) return;
        const newEnd = Math.floor(s.currentTime);
        if (newEnd > s.startSecond && newEnd <= s.duration) {
          setEndSecond(newEnd);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Compute preview URL (with the panel's preview pass when Monetize is on, so content behind a product plays here)
  const { grant: previewGrant, ready: previewReady } = usePreviewGrant(`media:${video.media.mediaId}`);
  const previewUrl = useMemo(() => {
    if (!previewPlayerId || !previewReady) return null;
    const mediaView = { data: video } as unknown as IMediaView;
    const linker = new MediaPlayerLinker(previewPlayerId, mediaView, isReseller);
    return MonetizeUtils.withPreviewGrant(linker.getPreviewUrl(false, { isPreview: true }), previewGrant);
  }, [previewPlayerId, video, isReseller, previewGrant, previewReady]);

  // Cue the preview player to an in/out point. The player listens for
  // time_change from the parent and seeks (Player.component handleTimeChangeMsg
  // — same channel legacy used); before it has loaded, its own guard drops the
  // message harmlessly. Only the explicit cue buttons call this: a seek costs a
  // real re-buffer, so it must never ride along on an edit.
  const seekPreview = useCallback(
    (second: number) => {
      const target = previewIframeRef.current?.contentWindow;
      if (!target || !previewUrl) return;
      const msg = PlayerMessageUtil.createPostMessage(
        {
          second,
          listId: null,
          mediaId: bcLong.getStr(video.media.mediaId),
          mediaType: video.media.mediaType,
        },
        PlayerMessageType.TimeChange
      );
      target.postMessage(msg, new URL(previewUrl, window.location.href).origin);
    },
    [previewUrl, video.media.mediaId, video.media.mediaType]
  );

  // TranscodeVod options sorted by resolution (highest first)
  const vodOptions = useMemo(() => {
    if (!transcodeVods?.data) return [];
    return transcodeVods.data
      .map((vod) => {
        const maxHeight = Math.max(
          ...vod.outputs.map((o) => o.videoVerticalSize ?? 0)
        );
        return {
          value: vod.transcodeVodId.toString(),
          label: `${maxHeight}p - ${vod.label}`,
          maxHeight,
        };
      })
      .sort((a, b) => b.maxHeight - a.maxHeight);
  }, [transcodeVods]);

  // Auto-select highest quality
  useEffect(() => {
    if (vodOptions.length > 0 && !selectedVodId) {
      setSelectedVodId(vodOptions[0].value);
    }
  }, [vodOptions, selectedVodId]);

  // Validation
  const clipDuration = endSecond - startSecond;
  const isValid =
    title.trim().length >= 1 && clipDuration >= 1 && startSecond < endSecond;

  const handleRangeChange = (start: number, end: number) => {
    setStartSecond(start);
    setEndSecond(end);
  };

  const handleSubmit = () => {
    if (!isValid) return;

    const updateReq = MediaUtils.transformFormToUpdateRequest(
      {
        ...MediaUtils.transformVideoToFormData(video),
        title: title.trim(),
        refId: '', // Clear refId for clips
      },
      false
    );
    updateReq.refId = null; // Ensure null for API

    const req: ClipCreateRequest = {
      start: startSecond,
      end: endSecond,
      updateMediaReq: updateReq,
      transcodeVodId: selectedVodId ? parseInt(selectedVodId, 10) : null,
    };

    clipMutation.mutate({ mediaId: video.media.mediaId, req }, { onSuccess });
  };

  return (
    <Stack gap="lg">
      {/* Source info */}
      <Text size="sm" c="dimmed">
        Source: "{video.media.title}" · {MediaUtils.formatTimecode(duration)}{' '}
        duration
      </Text>

      {/* Player */}
      {previewUrl && (
        <AspectRatio ratio={16 / 9}>
          <iframe
            ref={previewIframeRef}
            src={previewUrl}
            style={{ border: 'none', width: '100%', height: '100%' }}
            allowFullScreen
            title="Clip preview"
          />
        </AspectRatio>
      )}

      {/* Range selector with times and Set buttons */}
      <ClipRangeSelector
        duration={duration}
        startSecond={startSecond}
        endSecond={endSecond}
        playerTime={currentTime}
        onChange={handleRangeChange}
        onSeekPreview={seekPreview}
        onSetIn={() => {
          if (currentTime === null) return;
          const newStart = Math.floor(currentTime);
          if (newStart < endSecond) {
            setStartSecond(newStart);
          }
        }}
        onSetOut={() => {
          if (currentTime === null) return;
          const newEnd = Math.floor(currentTime);
          if (newEnd > startSecond && newEnd <= duration) {
            setEndSecond(newEnd);
          }
        }}
      />

      {/* Keyboard shortcut hint */}
      <Group gap="xs">
        <IconKeyboard size={16} color="var(--mantine-color-gray-5)" />
        <Text size="xs" c="dimmed">
          Tip: Press <Text span fw={600}>I</Text> / <Text span fw={600}>O</Text> to set points
          from the player (when player not focused). The eye buttons jump the preview to a point.
        </Text>
      </Group>

      {/* Keyframe notice */}
      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        Clip points may adjust to nearest keyframe
      </Alert>

      <Divider />

      {/* Title */}
      <TextInput
        label="Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        maxLength={VIDEO_CONSTRAINTS.TITLE_MAX}
        description={`${title.length}/${VIDEO_CONSTRAINTS.TITLE_MAX}`}
        required
        error={title.trim().length < 1 ? 'Title is required' : undefined}
      />

      {/* Quality selector */}
      <Select
        label="Output Quality"
        data={vodOptions}
        value={selectedVodId}
        onChange={setSelectedVodId}
        disabled={vodsLoading}
        placeholder={vodsLoading ? 'Loading...' : 'Select quality'}
      />

      {/* Actions */}
      <Group justify="flex-end" mt="md">
        <Button
          variant="default"
          onClick={onCancel}
          disabled={clipMutation.isPending}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          loading={clipMutation.isPending}
          disabled={!isValid}
        >
          Create Clip
        </Button>
      </Group>
    </Stack>
  );
}
